import React, { useState, useEffect, useMemo, useRef } from "react";
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Grid,
  Box,
  Chip,
  Divider,
  CircularProgress,
  FormControlLabel,
  RadioGroup,
  Radio,
  Autocomplete,
  Pagination,
  Stack,
  IconButton,
} from "@mui/material";
import {
  FaGift,
  FaPlus,
  FaCheck,
  FaHashtag,
  FaListOl,
  FaPlay,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaExclamationTriangle,
  FaUpload,
} from "react-icons/fa";
import {
  createGiftCertificates,
  getDropdownData,
  getDashboardData,
  selectGiftCertificate,
  GiftCertificateState,
  resetGiftCertificate,
} from "../slices/gift-certificate.slice";
import {
  getAdminSession,
  selectGetAdminSession,
} from "features/admin/presentation/slices/get-admin-session.slice";
import { FaLock } from "react-icons/fa";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

interface CreateGiftCertificateFormData {
  type: "customer" | "sponsorship" | "support";
  quantity: number | string;
  store_id: string;
  store_group_id: string;
  company_id: string;
  expiry_date: string;
  serial_mode: "normal" | "manual" | "series";
  manual_serials: string[];
  starting_serial: string;
}

export function CreateGiftCertificates() {
  const dispatch = useAppDispatch();
  const giftCertificateState = useAppSelector(selectGiftCertificate);

  const getAdminSessionState = useAppSelector(selectGetAdminSession);

  const is_admin = useMemo(() => {
    const userGroups =
      getAdminSessionState.data?.admin?.user_details?.groups || [];
    return userGroups.some((group) => group.id === 1 || group.id === 20);
  }, [getAdminSessionState.data?.admin?.user_details?.groups]);
  const isAdminCheckComplete = getAdminSessionState.data !== undefined;

  const [formData, setFormData] = useState<CreateGiftCertificateFormData>({
    type: "customer",
    quantity: 1,
    store_id: "",
    store_group_id: "",
    company_id: "",
    expiry_date: "",
    serial_mode: "normal",
    manual_serials: [""],
    starting_serial: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const serialsPerPage = 10;

  const [showSuccess, setShowSuccess] = useState(false);

  // ADD: New state for duplicate serial error
  const [showDuplicateError, setShowDuplicateError] = useState(false);
  const [duplicateErrorMessage, setDuplicateErrorMessage] = useState("");

  // ADD: File attachment state
  const [attachments, setAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ADD: Function to check if error is about duplicate serials
  const isDuplicateSerialError = (message: string): boolean => {
    const duplicateKeywords = [
      "already exist",
      "duplicate",
      "serial numbers exist",
      "already exists",
      "already in use",
      "not allowed",
    ];

    return duplicateKeywords.some((keyword) =>
      message.toLowerCase().includes(keyword.toLowerCase())
    );
  };

  const getSeriesRange = (): string => {
    const quantity = getQuantityAsNumber();
    const startingSerial = formData.starting_serial;

    if (!startingSerial || !quantity) return "";

    const startNum = parseInt(startingSerial);
    const endNum = startNum + quantity - 1;

    const startPadded = startNum.toString().padStart(6, "0");
    const endPadded = endNum.toString().padStart(6, "0");

    return `${startPadded} - ${endPadded}`;
  };

  // Helper function to convert quantity to number
  const getQuantityAsNumber = (): number => {
    return typeof formData.quantity === "string"
      ? parseInt(formData.quantity) || 1
      : formData.quantity;
  };

  const getTotalPages = (): number => {
    const quantity = getQuantityAsNumber();
    return Math.ceil(quantity / serialsPerPage);
  };

  const getCurrentPageSerials = () => {
    const quantity = getQuantityAsNumber();
    const startIndex = (currentPage - 1) * serialsPerPage;
    const endIndex = Math.min(startIndex + serialsPerPage, quantity);
    return { startIndex, endIndex, total: quantity };
  };

  useEffect(() => {
    dispatch(getAdminSession());
    dispatch(getDropdownData());
    dispatch(getDashboardData());
  }, [dispatch]);

  // UPDATED: Enhanced useEffect to handle duplicate errors in both success and fail responses
  useEffect(() => {
    // Check for duplicate errors in SUCCESS responses (current backend behavior)
    if (giftCertificateState.status === GiftCertificateState.success) {
      const message = giftCertificateState.message;

      if (isDuplicateSerialError(message)) {
        // Handle as duplicate error instead of success
        setDuplicateErrorMessage(message);
        setShowDuplicateError(true);

        // Reset the slice state to prevent showing success
        setTimeout(() => {
          dispatch(resetGiftCertificate());
        }, 100);

        return; // Exit early, don't show success
      }

      // Only show success if it's NOT a duplicate error
      if (!showSuccess) {
        setShowSuccess(true);
        // Reset form
        setFormData({
          type: "customer",
          quantity: 1,
          store_id: "",
          store_group_id: "",
          company_id: "",
          expiry_date: "",
          serial_mode: "normal",
          manual_serials: [""],
          starting_serial: "",
        });
        setCurrentPage(1);
        dispatch(getDashboardData());

        setTimeout(() => {
          setShowSuccess(false);
          dispatch(resetGiftCertificate());
        }, 5000);
      }
    }

    // Also check for duplicate errors in FAIL responses (proper behavior)
    if (giftCertificateState.status === GiftCertificateState.fail) {
      const errorMessage = giftCertificateState.message;

      if (isDuplicateSerialError(errorMessage)) {
        setDuplicateErrorMessage(errorMessage);
        setShowDuplicateError(true);
        setTimeout(() => {
          dispatch(resetGiftCertificate());
        }, 100);
      }
    }
  }, [giftCertificateState.status, dispatch, showSuccess]);

  // ADD: Function to manually close duplicate error
  const handleCloseDuplicateError = () => {
    setShowDuplicateError(false);
    setDuplicateErrorMessage("");
  };

  // Strictly numeric quantity handling
  const handleQuantityChange = (value: string) => {
    const numericOnly = value.replace(/\D/g, "");

    if (numericOnly === "") {
      setFormData((prev) => ({
        ...prev,
        quantity: "" as any,
      }));
      return;
    }

    const numericValue = parseInt(numericOnly);

    if (!isNaN(numericValue) && numericValue >= 0) {
      setFormData((prev) => {
        const finalQuantity = Math.max(1, Math.min(1000, numericValue));
        const newManualSerials = Array(finalQuantity)
          .fill("")
          .map((_, index) => prev.manual_serials[index] || "");

        return {
          ...prev,
          quantity: numericValue,
          manual_serials: newManualSerials,
        };
      });

      setCurrentPage(1);
    }
  };

  const handleQuantityBlur = () => {
    const quantityAsNumber = getQuantityAsNumber();

    if (formData.quantity === "" || quantityAsNumber < 1) {
      setFormData((prev) => ({
        ...prev,
        quantity: 1,
        manual_serials: [""],
      }));
      setCurrentPage(1);
    } else if (quantityAsNumber > 1000) {
      setFormData((prev) => ({
        ...prev,
        quantity: 1000,
        manual_serials: Array(1000)
          .fill("")
          .map((_, index) => prev.manual_serials[index] || ""),
      }));
      setCurrentPage(1);
    }
  };

  const handleManualSerialChange = (index: number, value: string) => {
    const numericValue = value.replace(/\D/g, "");

    setFormData((prev) => {
      const newManualSerials = [...prev.manual_serials];
      newManualSerials[index] = numericValue;
      return {
        ...prev,
        manual_serials: newManualSerials,
      };
    });
  };

  const handleStartingSerialChange = (value: string) => {
    const numericValue = value.replace(/\D/g, "");

    setFormData((prev) => ({
      ...prev,
      starting_serial: numericValue,
    }));
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    const totalPages = getTotalPages();
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Close any existing duplicate error before submitting
    if (showDuplicateError) {
      setShowDuplicateError(false);
      setDuplicateErrorMessage("");
    }

    const quantity = getQuantityAsNumber();

    // Use FormData for file upload support
    const submitFormData = new FormData();

    // Add form fields
    submitFormData.append("type", formData.type);
    submitFormData.append("quantity", quantity.toString());
    submitFormData.append("serial_mode", formData.serial_mode);

    if (formData.serial_mode === "manual") {
      const validSerials = formData.manual_serials.filter(
        (serial) => serial !== ""
      );
      validSerials.forEach((serial, index) => {
        submitFormData.append(`manual_serials[${index}]`, serial);
      });
    } else if (formData.serial_mode === "series") {
      submitFormData.append("starting_serial", formData.starting_serial);
    }

    if (formData.type === "customer") {
      submitFormData.append("store_group_id", formData.store_group_id);
    } else if (formData.type === "sponsorship") {
      submitFormData.append("store_group_id", formData.store_group_id);
      submitFormData.append("company_id", formData.company_id);
      if (formData.expiry_date) {
        submitFormData.append("expiry_date", formData.expiry_date);
      }
      // } else if (formData.type === "multiple") {
      //   submitFormData.append("store_group_id", formData.store_group_id);
    } else if (formData.type === "support") {
      submitFormData.append("store_id", formData.store_id);
      submitFormData.append("expiry_date", formData.expiry_date);
    }

    // Append attachments
    attachments.forEach((file) => {
      submitFormData.append(`attachments[]`, file);
    });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_DOMAIN_URL}api/gift-certificate/create`,
        {
          method: "POST",
          body: submitFormData,
          credentials: "include",
        }
      );

      const result = await response.json();

      if (result.status === "success") {
        // Reset form and attachments
        setFormData({
          type: "customer",
          quantity: 1,
          store_id: "",
          store_group_id: "",
          company_id: "",
          expiry_date: "",
          serial_mode: "normal",
          manual_serials: [""],
          starting_serial: "",
        });
        setAttachments([]); // Reset attachments

        // ADD: Reset file input value to allow same file to be selected again
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        setCurrentPage(1);

        setShowSuccess(true);
        dispatch(getDashboardData());

        setTimeout(() => {
          setShowSuccess(false);
          dispatch(resetGiftCertificate());
        }, 5000);
      } else {
        // Handle error using existing logic
        if (isDuplicateSerialError(result.message)) {
          setDuplicateErrorMessage(result.message);
          setShowDuplicateError(true);
        }
      }
    } catch (error) {
      console.error("Error creating GCs:", error);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleExpiryDateChange = (newValue: Date | null) => {
    setFormData((prev) => ({
      ...prev,
      expiry_date: newValue ? newValue.toISOString().split("T")[0] : "",
    }));
  };

  const handleTypeChange = (
    newType: "customer" | "sponsorship" | "support"
  ) => {
    setFormData((prev) => ({
      ...prev,
      type: newType,
      expiry_date: "",
      store_id: "",
      store_group_id: "",
      company_id: "",
    }));
  };

  const handleSerialModeChange = (newMode: "normal" | "manual" | "series") => {
    setFormData((prev) => {
      const quantity = getQuantityAsNumber();

      return {
        ...prev,
        serial_mode: newMode,
        manual_serials: newMode === "manual" ? Array(quantity).fill("") : [""],
        starting_serial: "",
      };
    });

    setCurrentPage(1);
  };

  // File upload handler
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const validFiles = Array.from(files).filter((file) => {
        const allowedTypes = [
          "application/pdf",
          "image/jpeg",
          "image/jpg",
          "image/png",
        ];
        const maxSize = 5 * 1024 * 1024; // 5MB

        if (!allowedTypes.includes(file.type)) {
          alert(`${file.name}: Only PDF, JPG, and PNG files are allowed`);
          return false;
        }

        if (file.size > maxSize) {
          alert(`${file.name}: File size must be less than 5MB`);
          return false;
        }

        return true;
      });

      setAttachments((prev) => [...prev, ...validFiles]);
    }

    // ADD: Reset input value to allow selecting the same file again
    if (event.target) {
      event.target.value = "";
    }
  };

  // ADD: Remove attachment handler
  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const isFormValid = () => {
    const quantity = getQuantityAsNumber();
    if (!quantity || quantity < 1 || quantity > 1000) return false;

    // Require at least one attachment
    if (attachments.length === 0) return false;

    if (formData.serial_mode === "manual") {
      const validSerials = formData.manual_serials.filter(
        (serial) =>
          serial.length > 0 && serial.length <= 6 && /^\d+$/.test(serial)
      );
      if (validSerials.length !== quantity) return false;

      const uniqueSerials = new Set(validSerials);
      if (uniqueSerials.size !== validSerials.length) return false;
    } else if (formData.serial_mode === "series") {
      if (
        !formData.starting_serial ||
        formData.starting_serial.length === 0 ||
        formData.starting_serial.length > 6
      )
        return false;
    }

    if (formData.type === "customer") {
      return (
        formData.store_group_id !== "" && formData.store_group_id !== undefined
      );
    } else if (formData.type === "sponsorship") {
      return (
        formData.store_group_id !== "" &&
        formData.store_group_id !== undefined &&
        formData.company_id !== "" &&
        formData.company_id !== undefined &&
        formData.expiry_date !== ""
      );
    } else if (formData.type === "support") {
      return (
        formData.store_id !== "" &&
        formData.store_id !== undefined &&
        formData.expiry_date !== ""
      );
    }

    return true;
  };

  const dropdownData = giftCertificateState.dropdownData;
  const lastCreated = giftCertificateState.lastCreated;

  // Show loading screen while checking admin status
  if (!isAdminCheckComplete) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <Card>
          <CardContent className="text-center py-12">
            <CircularProgress size={40} className="mb-4" />
            <Typography variant="h6" className="text-gray-600">
              Checking permissions...
            </Typography>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show access denied for non-admins
  if (!is_admin) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <Card>
          <CardContent className="text-center py-12">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaLock className="text-red-500" size={24} />
            </div>
            <Typography variant="h5" className="text-gray-800 mb-2">
              Access Denied
            </Typography>
            <Typography variant="body1" className="text-gray-600 mb-4">
              You don't have permission to create gift certificates.
            </Typography>
            <Typography variant="body2" color="textSecondary">
              This feature is only available to administrators.
            </Typography>
            <Alert severity="info" className="mt-4 max-w-md mx-auto">
              <Typography variant="body2">
                Please contact your administrator if you need to create gift
                certificates.
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className="min-h-screen bg-gray-50 p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
              <FaPlus className="text-white" size={20} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Create Gift Certificates
              </h1>
              <p className="text-gray-600">
                Generate multiple gift certificates with incremental serial
                numbers
              </p>
            </div>
          </div>
        </div>

        <Grid container spacing={4}>
          {/* Form */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <form onSubmit={handleSubmit}>
                  {/* Success Alert */}
                  {showSuccess && (
                    <Alert
                      severity="success"
                      className="mb-4"
                      icon={<FaCheck />}
                    >
                      {giftCertificateState.message}
                    </Alert>
                  )}

                  {/* ADD: Duplicate Serial Number Error Alert - Closeable */}
                  {showDuplicateError && (
                    <Alert
                      severity="warning"
                      className="mb-4"
                      icon={<FaExclamationTriangle />}
                      action={
                        <IconButton
                          aria-label="close"
                          color="inherit"
                          size="small"
                          onClick={handleCloseDuplicateError}
                        >
                          <FaTimes />
                        </IconButton>
                      }
                      sx={{
                        backgroundColor: "#fff3cd",
                        border: "1px solid #ffeaa7",
                        color: "#856404",
                        "& .MuiAlert-icon": {
                          color: "#f39c12",
                        },
                      }}
                    >
                      <div>
                        <Typography
                          variant="subtitle2"
                          className="font-semibold mb-1"
                        >
                          Duplicate Serial Numbers Detected
                        </Typography>
                        <Typography variant="body2">
                          {duplicateErrorMessage}
                        </Typography>
                        <Typography
                          variant="body2"
                          className="mt-2 text-sm opacity-90"
                        >
                          Please choose different serial numbers and try again.
                        </Typography>
                      </div>
                    </Alert>
                  )}

                  {/* UPDATED: General Error Alert - Only show non-duplicate errors */}
                  {giftCertificateState.status === GiftCertificateState.fail &&
                    !isDuplicateSerialError(giftCertificateState.message) && (
                      <Alert severity="error" className="mb-4">
                        {giftCertificateState.message}
                      </Alert>
                    )}

                  <Grid container spacing={3}>
                    {/* Type Selection */}
                    <Grid item xs={12}>
                      <Typography variant="h6" className="mb-3 font-semibold">
                        Gift Certificate Type
                      </Typography>
                      <FormControl component="fieldset">
                        <RadioGroup
                          row
                          value={formData.type}
                          onChange={(e) =>
                            handleTypeChange(
                              e.target.value as
                                | "customer"
                                | "sponsorship"
                                // | "multiple"
                                | "support"
                            )
                          }
                        >
                          <FormControlLabel
                            value="customer"
                            control={<Radio />}
                            label={
                              <div className="flex items-center space-x-2">
                                <Chip
                                  label="Customer"
                                  color="success"
                                  size="small"
                                />
                                <span>All stores, no expiry</span>
                              </div>
                            }
                          />
                          <FormControlLabel
                            value="sponsorship"
                            control={<Radio />}
                            label={
                              <div className="flex items-center space-x-2">
                                <Chip
                                  label="Sponsorship"
                                  color="secondary"
                                  size="small"
                                />
                                <span>With expiry, multiple stores</span>
                              </div>
                            }
                          />
                          {/* <FormControlLabel
                            value="multiple"
                            control={<Radio />}
                            label={
                              <div className="flex items-center space-x-2">
                                <Chip
                                  label="Multiple"
                                  color="info"
                                  size="small"
                                />
                                <span>
                                  Multiple stores, no expiry, no company
                                </span>
                              </div>
                            }
                          /> */}
                          <FormControlLabel
                            value="support"
                            control={<Radio />}
                            label={
                              <div className="flex items-center space-x-2">
                                <Chip
                                  label="Support"
                                  color="warning"
                                  size="small"
                                />
                                <span>Single store with expiry</span>
                              </div>
                            }
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    {/* Serial Number Mode Selection */}
                    <Grid item xs={12}>
                      <Typography variant="h6" className="mb-3 font-semibold">
                        Serial Number Generation
                      </Typography>
                      <FormControl component="fieldset">
                        <RadioGroup
                          row
                          value={formData.serial_mode}
                          onChange={(e) =>
                            handleSerialModeChange(
                              e.target.value as "normal" | "manual" | "series"
                            )
                          }
                        >
                          <FormControlLabel
                            value="normal"
                            control={<Radio />}
                            label={
                              <div className="flex items-center space-x-2">
                                <FaHashtag
                                  className="text-blue-500"
                                  size={14}
                                />
                                <span>Auto-increment</span>
                              </div>
                            }
                          />
                          <FormControlLabel
                            value="manual"
                            control={<Radio />}
                            label={
                              <div className="flex items-center space-x-2">
                                <FaListOl
                                  className="text-green-500"
                                  size={14}
                                />
                                <span>Manual Input</span>
                              </div>
                            }
                          />
                          <FormControlLabel
                            value="series"
                            control={<Radio />}
                            label={
                              <div className="flex items-center space-x-2">
                                <FaPlay className="text-purple-500" size={14} />
                                <span>Specific Series</span>
                              </div>
                            }
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    {/* Enhanced Quantity Field */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Quantity"
                        type="number"
                        value={formData.quantity}
                        onChange={(e) => handleQuantityChange(e.target.value)}
                        onBlur={handleQuantityBlur}
                        onKeyPress={(e) => {
                          if (
                            !/[0-9]/.test(e.key) &&
                            ![
                              "Backspace",
                              "Delete",
                              "ArrowLeft",
                              "ArrowRight",
                              "Tab",
                            ].includes(e.key)
                          ) {
                            e.preventDefault();
                          }
                        }}
                        inputProps={{
                          min: 1,
                          max: 1000,
                          step: 1,
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                        }}
                        helperText={
                          typeof formData.quantity === "string" &&
                          formData.quantity === ""
                            ? "Enter quantity (1-1000)"
                            : (() => {
                                const qty = getQuantityAsNumber();
                                return `Maximum 1000 certificates per batch${
                                  qty > 1000
                                    ? " - Value will be capped at 1000"
                                    : ""
                                }`;
                              })()
                        }
                        error={(() => {
                          const qty = getQuantityAsNumber();
                          return qty < 1 || qty > 1000;
                        })()}
                        required
                      />
                    </Grid>

                    {/* Starting Serial Number (for series mode) */}
                    {formData.serial_mode === "series" && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Starting Serial Number"
                          value={formData.starting_serial}
                          onChange={(e) =>
                            handleStartingSerialChange(e.target.value)
                          }
                          placeholder="1"
                          helperText="Enter 1-6 digits (will be padded to 6 digits when created)"
                          inputProps={{
                            maxLength: 6,
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                          }}
                          required
                        />

                        {/* ADD: Range Display */}
                        {formData.starting_serial &&
                          getQuantityAsNumber() > 0 && (
                            <Box className="mt-2 p-2 bg-blue-50 rounded">
                              <Typography
                                variant="body2"
                                className="font-medium text-blue-800"
                              >
                                Series Range: {getSeriesRange()}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {getQuantityAsNumber()} certificate
                                {getQuantityAsNumber() > 1 ? "s" : ""} will be
                                created
                              </Typography>
                            </Box>
                          )}
                      </Grid>
                    )}

                    {/* Manual Serial Numbers with Pagination */}
                    {formData.serial_mode === "manual" && (
                      <Grid item xs={12}>
                        <Typography variant="h6" className="mb-3 font-semibold">
                          Manual Serial Numbers
                        </Typography>

                        {(() => {
                          const { startIndex, endIndex, total } =
                            getCurrentPageSerials();
                          const totalPages = getTotalPages();

                          return (
                            <>
                              {/* Serial Number Input Fields */}
                              <Grid container spacing={2}>
                                {Array.from({
                                  length: endIndex - startIndex,
                                }).map((_, pageIndex) => {
                                  const actualIndex = startIndex + pageIndex;
                                  return (
                                    <Grid
                                      item
                                      xs={12}
                                      sm={6}
                                      md={4}
                                      lg={3}
                                      key={actualIndex}
                                    >
                                      <TextField
                                        fullWidth
                                        label={`Serial #${actualIndex + 1}`}
                                        value={
                                          formData.manual_serials[
                                            actualIndex
                                          ] || ""
                                        }
                                        onChange={(e) =>
                                          handleManualSerialChange(
                                            actualIndex,
                                            e.target.value
                                          )
                                        }
                                        placeholder="1"
                                        inputProps={{
                                          maxLength: 6,
                                          inputMode: "numeric",
                                          pattern: "[0-9]*",
                                        }}
                                        helperText="1-6 digits"
                                        required
                                        size="small"
                                      />
                                    </Grid>
                                  );
                                })}
                              </Grid>

                              {/* Pagination Component */}
                              {totalPages > 1 && (
                                <Box className="mt-4 flex justify-center">
                                  <Stack spacing={2}>
                                    <Pagination
                                      count={totalPages}
                                      page={currentPage}
                                      onChange={handlePageChange}
                                      color="primary"
                                      size="large"
                                      showFirstButton
                                      showLastButton
                                      boundaryCount={2}
                                      siblingCount={1}
                                    />
                                  </Stack>
                                </Box>
                              )}
                            </>
                          );
                        })()}

                        {/* UPDATED: Alert with duplicate warning */}
                        <Alert severity="info" className="mt-3">
                          <Typography variant="body2">
                            Serial numbers will be automatically padded to 6
                            digits (e.g., "1" becomes "000001") when the
                            certificates are created. Make sure all numbers are
                            unique across all pages to avoid duplicate errors.
                          </Typography>
                        </Alert>
                      </Grid>
                    )}

                    <Divider className="w-full my-4" />

                    {/* Customer GC Fields */}
                    {formData.type === "customer" && (
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          options={dropdownData?.store_groups || []}
                          getOptionLabel={(option) =>
                            `${option.group_name} (${option.store_count} stores)`
                          }
                          value={
                            dropdownData?.store_groups?.find(
                              (group) =>
                                group.id.toString() === formData.store_group_id
                            ) || null
                          }
                          onChange={(event, newValue) => {
                            handleInputChange(
                              "store_group_id",
                              newValue?.id.toString() || ""
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Store Group"
                              placeholder="Search store groups..."
                              variant="outlined"
                              required
                              helperText="Certificate can be used at any store in this group. No expiry date."
                            />
                          )}
                          renderOption={(props, option) => {
                            const { key, ...otherProps } = props;
                            return (
                              <Box component="li" key={key} {...otherProps}>
                                <div className="flex flex-col w-full">
                                  <span className="font-medium text-sm">
                                    {option.group_name}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {option.store_count} stores
                                  </span>
                                </div>
                              </Box>
                            );
                          }}
                          noOptionsText="No store groups found"
                        />
                      </Grid>
                    )}

                    {/* Sponsorship GC Fields */}
                    {formData.type === "sponsorship" && (
                      <>
                        <Grid item xs={12} md={4}>
                          <Autocomplete
                            options={dropdownData?.store_groups || []}
                            getOptionLabel={(option) =>
                              `${option.group_name} (${option.store_count} stores)`
                            }
                            value={
                              dropdownData?.store_groups?.find(
                                (group) =>
                                  group.id.toString() ===
                                  formData.store_group_id
                              ) || null
                            }
                            onChange={(event, newValue) => {
                              handleInputChange(
                                "store_group_id",
                                newValue?.id.toString() || ""
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Store Group"
                                placeholder="Search store groups..."
                                variant="outlined"
                                required
                              />
                            )}
                            renderOption={(props, option) => {
                              const { key, ...otherProps } = props;
                              return (
                                <Box component="li" key={key} {...otherProps}>
                                  <div className="flex flex-col w-full">
                                    <span className="font-medium text-sm">
                                      {option.group_name}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {option.store_count} stores
                                    </span>
                                  </div>
                                </Box>
                              );
                            }}
                            noOptionsText="No store groups found"
                          />
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <Autocomplete
                            options={dropdownData?.companies || []}
                            getOptionLabel={(option) => option.name}
                            value={
                              dropdownData?.companies?.find(
                                (company) =>
                                  company.id.toString() === formData.company_id
                              ) || null
                            }
                            onChange={(event, newValue) => {
                              handleInputChange(
                                "company_id",
                                newValue?.id.toString() || ""
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Company"
                                placeholder="Search companies..."
                                variant="outlined"
                                required
                              />
                            )}
                            renderOption={(props, option) => {
                              const { key, ...otherProps } = props;
                              return (
                                <Box component="li" key={key} {...otherProps}>
                                  <div className="flex items-center w-full">
                                    <span className="font-medium text-sm">
                                      {option.name}
                                    </span>
                                  </div>
                                </Box>
                              );
                            }}
                            noOptionsText="No companies found"
                          />
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <DatePicker
                            label="Expiry Date"
                            value={
                              formData.expiry_date
                                ? new Date(formData.expiry_date + "T00:00:00")
                                : null
                            }
                            onChange={(newValue: Date | null) => {
                              if (newValue) {
                                const localDate = new Date(
                                  newValue.getTime() -
                                    newValue.getTimezoneOffset() * 60000
                                );
                                const formattedDate = localDate
                                  .toISOString()
                                  .split("T")[0];
                                setFormData((prev) => ({
                                  ...prev,
                                  expiry_date: formattedDate,
                                }));
                              } else {
                                setFormData((prev) => ({
                                  ...prev,
                                  expiry_date: "",
                                }));
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                fullWidth
                                variant="outlined"
                                required
                                helperText="Select the expiration date for the gift certificates"
                              />
                            )}
                            minDate={new Date()}
                            inputFormat="yyyy-MM-dd"
                          />
                        </Grid>
                      </>
                    )}

                    {/* Multiple GC Fields */}
                    {/* {formData.type === "multiple" && (
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          options={dropdownData?.store_groups || []}
                          getOptionLabel={(option) =>
                            `${option.group_name} (${option.store_count} stores)`
                          }
                          value={
                            dropdownData?.store_groups?.find(
                              (group) =>
                                group.id.toString() === formData.store_group_id
                            ) || null
                          }
                          onChange={(event, newValue) => {
                            handleInputChange(
                              "store_group_id",
                              newValue?.id.toString() || ""
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Store Group"
                              placeholder="Search store groups..."
                              variant="outlined"
                              required
                            />
                          )}
                          renderOption={(props, option) => {
                            const { key, ...otherProps } = props;
                            return (
                              <Box component="li" key={key} {...otherProps}>
                                <div className="flex flex-col w-full">
                                  <span className="font-medium text-sm">
                                    {option.group_name}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {option.store_count} stores
                                  </span>
                                </div>
                              </Box>
                            );
                          }}
                          noOptionsText="No store groups found"
                        />
                      </Grid>
                    )} */}

                    {/* Support GC Fields */}
                    {formData.type === "support" && (
                      <>
                        <Grid item xs={12} md={6}>
                          <Autocomplete
                            options={dropdownData?.stores || []}
                            getOptionLabel={(option) =>
                              `${option.name} - ${option.address}`
                            }
                            value={
                              dropdownData?.stores?.find(
                                (store) =>
                                  store.id.toString() === formData.store_id
                              ) || null
                            }
                            onChange={(event, newValue) => {
                              handleInputChange(
                                "store_id",
                                newValue?.id.toString() || ""
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Store"
                                placeholder="Search by store name or address..."
                                variant="outlined"
                                required
                              />
                            )}
                            renderOption={(props, option) => {
                              const { key, ...otherProps } = props;
                              return (
                                <Box component="li" key={key} {...otherProps}>
                                  <div className="flex flex-col w-full">
                                    <span className="font-medium text-sm">
                                      {option.name}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {option.address}
                                    </span>
                                  </div>
                                </Box>
                              );
                            }}
                            noOptionsText="No stores found"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <DatePicker
                            label="Expiry Date"
                            value={
                              formData.expiry_date
                                ? new Date(formData.expiry_date + "T00:00:00")
                                : null
                            }
                            onChange={(newValue: Date | null) => {
                              if (newValue) {
                                const localDate = new Date(
                                  newValue.getTime() -
                                    newValue.getTimezoneOffset() * 60000
                                );
                                const formattedDate = localDate
                                  .toISOString()
                                  .split("T")[0];
                                setFormData((prev) => ({
                                  ...prev,
                                  expiry_date: formattedDate,
                                }));
                              } else {
                                setFormData((prev) => ({
                                  ...prev,
                                  expiry_date: "",
                                }));
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                fullWidth
                                variant="outlined"
                                required
                                helperText="Support GCs must have an expiry date"
                              />
                            )}
                            minDate={new Date()}
                            inputFormat="yyyy-MM-dd"
                          />
                        </Grid>
                      </>
                    )}

                    {/* File Attachments Section */}
                    <Grid item xs={12}>
                      <Divider className="w-full my-4" />
                      <div className="space-y-2">
                        <Typography
                          variant="subtitle2"
                          className="font-medium text-red-600"
                        >
                          Attachments (Required) *
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          className="mb-2"
                        >
                          PDF, JPG, PNG - Max 5MB each. At least one attachment
                          is required.
                        </Typography>
                        <input
                          type="file"
                          multiple
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleFileUpload}
                          style={{ display: "none" }}
                          ref={fileInputRef}
                        />
                        <Button
                          variant="outlined"
                          onClick={() => fileInputRef.current?.click()}
                          startIcon={<FaUpload />}
                          size="small"
                          color={attachments.length === 0 ? "error" : "primary"}
                        >
                          Add Attachments {attachments.length === 0 && "*"}
                        </Button>

                        {/* Show error message if no attachments */}
                        {attachments.length === 0 && (
                          <Typography
                            variant="body2"
                            color="error"
                            className="mt-1"
                          >
                            At least one attachment is required before creating
                            gift certificates.
                          </Typography>
                        )}

                        {attachments.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {attachments.map((file, index) => (
                              <div
                                key={index}
                                className="flex items-center justify-between bg-gray-100 p-2 rounded"
                              >
                                <span className="text-sm truncate flex-1 mr-2">
                                  {file.name} (
                                  {(file.size / 1024 / 1024).toFixed(2)} MB)
                                </span>
                                <IconButton
                                  size="small"
                                  onClick={() => removeAttachment(index)}
                                  color="error"
                                >
                                  <FaTimes />
                                </IconButton>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </Grid>

                    {/* Submit Button */}
                    <Grid item xs={12}>
                      <Box className="flex justify-end space-x-3 pt-4">
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          disabled={
                            !isFormValid() ||
                            giftCertificateState.status ===
                              GiftCertificateState.inProgress
                          }
                          startIcon={
                            giftCertificateState.status ===
                            GiftCertificateState.inProgress ? (
                              <CircularProgress size={20} />
                            ) : (
                              <FaGift />
                            )
                          }
                          className="bg-orange-500 hover:bg-orange-600"
                        >
                          {giftCertificateState.status ===
                          GiftCertificateState.inProgress
                            ? "Creating..."
                            : (() => {
                                const qty = getQuantityAsNumber();
                                return `Create ${qty} Gift Certificate${
                                  qty > 1 ? "s" : ""
                                }`;
                              })()}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          </Grid>

          {/* Last Created Info - Unchanged */}
          <Grid item xs={12} md={4}>
            <div className="space-y-4">
              {/* Last Customer GC */}
              {lastCreated?.customer && (
                <Card>
                  <CardContent>
                    <Typography
                      variant="h6"
                      className="mb-3 font-semibold flex items-center"
                    >
                      <Chip
                        label="Customer"
                        size="small"
                        color="success"
                        className="mr-2"
                      />
                      Last Created
                    </Typography>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Serial:</span>
                        <span className="font-mono font-semibold">
                          #{lastCreated.customer.serial_number}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Store Group:</span>
                        <span className="text-sm">
                          {lastCreated.customer.group_name || "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Created:</span>
                        <span className="text-sm">
                          {new Date(
                            lastCreated.customer.created_at
                          ).toLocaleDateString()}
                        </span>
                      </div>
                      <Chip
                        label={lastCreated.customer.current_status}
                        size="small"
                        color="primary"
                      />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Info Card - UPDATED with duplicate warning */}
              <Card className="bg-blue-50 border-blue-200">
                <CardContent>
                  <Typography
                    variant="h6"
                    className="mb-3 font-semibold text-blue-800"
                  >
                    Serial Number Info
                  </Typography>
                  <div className="space-y-2 text-sm text-blue-700">
                    <p>• Customer GCs have their own serial number sequence</p>
                    <p>
                      • Sponsorship and Support GCs share the same serial number
                      sequence
                    </p>
                    <p>
                      • Serial numbers are 6-digit, zero-padded (e.g., 000001)
                    </p>
                    <p>• Auto-increment uses the next available number</p>
                    <p>
                      • Manual input allows custom serial numbers (10 per page)
                    </p>
                    <p>
                      • Specific series shows range based on starting number and
                      quantity
                    </p>
                    <p className="text-amber-700 font-medium">
                      ⚠️ Duplicate serial numbers within unified groups will be
                      rejected
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </Grid>
        </Grid>
      </div>
    </LocalizationProvider>
  );
}
